import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-05ba225b"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "map" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  id: "map",
  class: "map__window"
}, null, -1))
const _hoisted_3 = [
  _hoisted_2
]

import { DEFAULT_COORDINATES } from '@/enums/services'
import {
  computed,
  reactive,
  PropType,
  onUnmounted,
  createApp,
  watch,
} from 'vue'
import { GoogleCoordinates } from '@/models/location.model'
import { PublicService } from '@/models/public/bookingWidget.model'
import InfoWindowContent from '@/components/public/route-planner/InfoWindowContent.vue'
import { Route, RouteStop } from '@/models/public/routePlanner.model'
import isBefore from 'date-fns/isBefore'

interface Location {
  lat: number
  lng: number
  title: string
  services: PublicService[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RouteMap',
  props: {
  route: {
    type: Object as PropType<Route | null>,
    default: null,
  },
  services: {
    type: Array as PropType<PublicService[]>,
    required: true,
  },
  selectedService: {
    type: Object as PropType<PublicService | null>,
    default: null,
  },
  disableRoutePlanning: {
    type: Boolean,
    default: false,
  },
},
  emits: ['onAdd', 'onUpdateSegments'],
  setup(__props, { emit: emits }) {

const props = __props




const apiKey = process.env.VUE_APP_GOOGLE_API_KEY
const zoom = 8
let map = reactive(undefined)
let directionsService = reactive(undefined)
let directionsRenderer = reactive(undefined)
let infoWindow = reactive(undefined)
let markers = reactive([])
const coordinates = reactive<GoogleCoordinates>({
  lat: DEFAULT_COORDINATES.LATITUDE_ESTONIA,
  lng: DEFAULT_COORDINATES.LONGITUDE_ESTONIA,
})

const uniqueLocations = computed(() =>
  props.services.reduce((acc, service) => {
    if (!service.location) return acc

    const { latitude_decimal_degree, longitude_decimal_degree } =
      service.location
    const existingLocationIndex = acc.findIndex(
      (location) =>
        location.lat === +latitude_decimal_degree &&
        location.lng === +longitude_decimal_degree,
    )
    if (existingLocationIndex > -1) {
      acc[existingLocationIndex].services.push(service)
    } else {
      acc.push({
        lat: +latitude_decimal_degree,
        lng: +longitude_decimal_degree,
        title: service.location.full_address,
        services: [service],
      })
    }
    return acc
  }, [] as Location[]),
)

const url = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`
const scripts = document.getElementsByTagName('script')

onUnmounted(() => {
  // remove old script if there is one
  for (let sc = 0; sc < scripts.length; sc++) {
    if (scripts[sc].src === url) {
      scripts[sc].parentNode?.removeChild(scripts[sc])
    }
  }
})

watch(
  () => [props.selectedService, uniqueLocations.value],
  () => {
    updateMarkers()
  },
)

watch(
  () => [props.route?.stops, props.route?.start_point, props.route?.end_point],
  ([stops, startPoint, endPoint], [oldStops, oldStartPoint, oldEndPoint]) => {
    const startPointChanged =
      startPoint?.latitude_decimal_degree !==
        oldStartPoint?.latitude_decimal_degree ||
      startPoint?.longitude_decimal_degree !==
        oldStartPoint?.longitude_decimal_degree
    const endPointChanged =
      endPoint?.latitude_decimal_degree !==
        oldEndPoint?.latitude_decimal_degree ||
      endPoint?.longitude_decimal_degree !==
        oldEndPoint?.longitude_decimal_degree
    const stopsChanged =
      stops?.length !== oldStops?.length ||
      stops?.some((stop, index) => {
        const oldStop = oldStops?.[index]
        return (
          stop?.latitude_decimal_degree !== oldStop?.latitude_decimal_degree ||
          stop?.longitude_decimal_degree !== oldStop?.longitude_decimal_degree
        )
      })
    const shouldRecalculate =
      startPointChanged || endPointChanged || stopsChanged
    if (shouldRecalculate) {
      calculateAndDisplayRoute()
    }
  },
)

// add google script to document head
const script = document.createElement('script')
script.src = url
script.async = true
document.head.appendChild(script)

async function initMap() {
  // @ts-ignore
  // eslint-disable-next-line no-undef
  const { Map, InfoWindow, DirectionsService, DirectionsRenderer } = google.maps
  directionsService = new DirectionsService()
  directionsRenderer = new DirectionsRenderer()
  map = new Map(document.getElementById('map'), {
    center: coordinates,
    zoom,
    mapId: 'ROUTE_PLANNER',
  })
  directionsRenderer.setMap(map)
  infoWindow = new InfoWindow()
  drawMarkers()
  calculateAndDisplayRoute()
}

function calculateAndDisplayRoute() {
  if (
    !props.route?.start_point ||
    !Object.keys(props.route.start_point).length ||
    !props.route?.end_point ||
    !Object.keys(props.route.end_point).length
  )
    return

  // @ts-ignore
  // eslint-disable-next-line no-undef
  const { LatLng, TravelMode } = google.maps
  const waypoints =
    props.route?.stops.map((stop: RouteStop) => ({
      location: new LatLng(
        +stop.service.location.latitude_decimal_degree,
        +stop.service.location.longitude_decimal_degree,
      ),
      stopover: true,
    })) || []
  let departureTime =
    typeof props.route.start_time === 'string'
      ? new Date(props.route.start_time)
      : props.route.start_time || new Date()
  const isPast = isBefore(departureTime, new Date())
  if (isPast) {
    // departure time can't be in the past
    departureTime = new Date()
  }

  directionsService
    .route({
      origin: new LatLng(
        +props.route.start_point.latitude_decimal_degree,
        +props.route.start_point.longitude_decimal_degree,
      ),
      destination: new LatLng(
        +props.route.end_point.latitude_decimal_degree,
        +props.route.end_point.longitude_decimal_degree,
      ),
      waypoints: waypoints,
      optimizeWaypoints: true,
      travelMode: TravelMode.DRIVING,
      drivingOptions: {
        departureTime,
        trafficModel: 'pessimistic',
      },
    })
    .then((response) => {
      directionsRenderer.setDirections(response)
      const route = response.routes[0]
      emits('onUpdateSegments', route)
    })
    .catch((e) => console.error('Directions request failed due to ' + e))
}

const updateMarkers = () => {
  markers = markers.reduce((acc, marker) => {
    const removedMarker = !uniqueLocations.value.find(
      ({ lat, lng }) =>
        marker.position.lat === lat && marker.position.lng === lng,
    )
    if (removedMarker) {
      marker.setMap(null)
    } else {
      acc.push(marker)
    }
    return acc
  }, [])
  drawMarkers()
}

const drawMarkers = async () => {
  // @ts-ignore
  // eslint-disable-next-line no-undef
  const { importLibrary } = google.maps
  const { AdvancedMarkerElement, PinElement } = await importLibrary('marker')

  uniqueLocations.value.forEach(({ lat, lng, title, services }) => {
    const isSelected = hasAnyServiceInSelectedServices(services)
    const pin = new PinElement({
      background: isSelected ? '#6cecff' : '#009e21',
      borderColor: isSelected ? '#00acff' : '#74ff82',
      glyphColor: isSelected ? '#00acff' : '#74ff82',
    })
    let marker = markers.find(
      (marker) => marker.position.lat === lat && marker.position.lng === lng,
    )
    if (marker) {
      marker.content = pin.element
      marker.zIndex = isSelected ? 1 : 0
    } else {
      marker = new AdvancedMarkerElement({
        map: map,
        title,
        position: { lat, lng },
        gmpClickable: true,
        content: pin.element,
        zIndex: isSelected ? 1 : 0,
      })
      marker.locationData = { lat, lng }

      marker.addListener('gmp-click', () => {
        const currentLocation = uniqueLocations.value.find(
          (loc) =>
            loc.lat === marker.locationData.lat &&
            loc.lng === marker.locationData.lng,
        )
        openInfoWindow(marker, currentLocation ? currentLocation.services : [])
      })
      markers.push(marker)
    }
    if (isSelected) {
      openInfoWindow(marker, services)
    }
  })

  function getContent(services: PublicService[]) {
    const app = createApp(InfoWindowContent, {
      services,
      onAdd: (value) => {
        emits('onAdd', value)
      },
      disableRoutePlanning: props.disableRoutePlanning,
    })
    const content = document.createElement('div')
    app.mount(content)
    return content
  }

  function openInfoWindow(marker, services) {
    infoWindow.setContent(getContent(services))
    infoWindow.open({
      anchor: marker,
      map: map,
    })
  }
}

function hasAnyServiceInSelectedServices(services: PublicService[]) {
  return services.some((service) => props.selectedService?.id === service.id)
}

window.initMap = initMap

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_3))
}
}

})